<template>
  <Sidebar :visible="appState.isAppSettingsPanelVisible" position="right" :showCloseIcon="false" :modal="false" class="layout-config p-sidebar-sm fs-small p-0" @keydown.esc="appState.isAppSettingsPanelVisible = false">
    <div class="layout-config-panel flex flex-column">
      <div class="px-5 py-4">
        <h2>Settings</h2>
        <Button label="Change Your Password" class="mb-3 p-button-sm p-button-outlined p-button-rounded" icon="material-symbols-outlined key" @click="AuthenticationService.redirectToForgotPasswordURL()" v-focus />
        <p class="m-0">Homepage, menu, and layout settings are saved to your account as they are changed.</p>
      </div>
      <hr class="m-0" />
      <div class="layout-config-options py-3 px-5">
        <h3 class="mt-2">Homepage</h3>
        <Dropdown v-model="appSettingsForm.homepage" :options="appSettingsForm.homepages" optionLabel="label" class="w-full homepage-dropdown" appendTo="self" @change="changeHomepage()" />
        <div class="hidden lg:block">
          <h3 class="mt-5">Menu Mode</h3>
          <fieldset class="flex">
            <legend class="hidden-but-accessible">Menu Mode Options</legend>
            <div class="flex flex-column">
              <div class="flex align-items-center">
                <RadioButton name="menuMode" value="static" v-model="appState.user.preferences.appMenuMode" inputId="menuMode1" @change="changeMenuMode('static')"></RadioButton>
                <label for="menuMode1" class="ml-2">Static</label>
              </div>
              <div class="flex align-items-center mt-3">
                <RadioButton name="menuMode" value="slim" v-model="appState.user.preferences.appMenuMode" inputId="menuMode4" @change="changeMenuMode('slim')"></RadioButton>
                <label for="menuMode4" class="ml-2">Slim</label>
              </div>
            </div>
            <div class="flex flex-column ml-4">
              <div class="flex align-items-center">
                <RadioButton name="menuMode" value="overlay" v-model="appState.user.preferences.appMenuMode" inputId="menuMode3" @change="changeMenuMode('overlay')"></RadioButton>
                <label for="menuMode3" class="ml-2">Overlay</label>
              </div>
              <div class="flex align-items-center mt-3">
                <RadioButton name="menuMode" value="horizontal" v-model="appState.user.preferences.appMenuMode" inputId="menuMode2" @change="changeMenuMode('horizontal')"></RadioButton>
                <label for="menuMode2" class="ml-2">Horizontal</label>
              </div>
            </div>
          </fieldset>
        </div>
        <h3 class="mt-5">Layout Mode</h3>
        <fieldset class="flex">
          <legend class="hidden-but-accessible">Layout Mode Options</legend>
          <div class="flex align-items-center">
            <RadioButton inputId="light" name="layoutMode" value="light" v-model="appState.user.preferences.appLayoutMode" @change="changeLayoutMode('light')" />
            <label for="light" class="ml-2">Light</label>
          </div>
          <div class="flex align-items-center ml-4">
            <RadioButton inputId="dark" name="layoutMode" value="dark" v-model="appState.user.preferences.appLayoutMode" @change="changeLayoutMode('dark')" />
            <label for="dark" class="ml-2">Dark</label>
          </div>
        </fieldset>
      </div>
    </div>
  </Sidebar>
</template>

<script setup>
  import { inject, ref } from "vue";
  import { updateUserPreference } from "@/services/coreCommonDataService/user.js";
  import AuthenticationService from "@/services/AuthenticationService.js";
  import { useToastUtil } from "@/utilities/useToastUtil.js";

  const appState = inject("appState");
  const toastUtil = useToastUtil();

  // setup a few things for homepage dropdown
  const activatedMenuItems = appState.menuItems.map(mI => mI.items).flat().filter(mI => mI.isActivated);
  const appSettingsForm = ref({ 
    homepages: activatedMenuItems,
    homepage: activatedMenuItems[0]
  });
  // check for existence of user's preferred homepage and set dropdown
  if (appState.user.preferences.appHomepage) {
    const appHomepage = JSON.parse(appState.user.preferences.appHomepage);
    const optionMatchingAppHomepage = appSettingsForm.value.homepages.find(h => h.to === appHomepage.to || h.label === appHomepage.label);
    if (optionMatchingAppHomepage) appSettingsForm.value.homepage = optionMatchingAppHomepage;
  } 

  // functions for when the user changes settings on this page, update appState.user.preferences object and make endpoint call
  function changeHomepage() {
    appState.user.preferences.appHomepage = JSON.stringify({ label: appSettingsForm.value.homepage.label, to: appSettingsForm.value.homepage.to });
    updateUserPreference({ name: "appHomepage", value: appState.user.preferences.appHomepage }).then(data => {
      if (data.httpError) return;
      toastUtil.clearAll();
      toastUtil.success("Your homepage has been successfully changed.");
    });
  }
  function changeMenuMode(menuMode) {
    appState.user.preferences.appMenuMode = menuMode;
    updateUserPreference({ name: "appMenuMode", value: appState.user.preferences.appMenuMode });
  }
  function changeLayoutMode(layoutMode) {
    appState.user.preferences.appLayoutMode = layoutMode;
    updateUserPreference({ name: "appLayoutMode", value: appState.user.preferences.appLayoutMode });
  }

</script>

<style lang="scss" scoped>
  :deep(.homepage-dropdown) {
    .p-inputtext, .p-component {
      font-size: 0.858rem;
    }
  }
  fieldset {
    border: none;
    padding: 0;
  }
</style>